"use client";

import {
  LayoutGridIcon,
  User as UserIcon,
  LogIn,
  LogOut,
  ChevronDown,
  LayoutDashboard,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useState, useCallback } from "react";
import type { MenuItem } from "shared";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "shared/components/ui/dropdown-menu";

import { useAuth } from "@/lib/contexts";
import { signOut } from "@/lib/firebase/auth";
import { useIsAdmin } from "@/lib/utils/orgAuth";

type TopNavProps = {
  menuItems: MenuItem[];
};

export function TopNav({ menuItems }: TopNavProps) {
  const pathname = usePathname();
  const router = useRouter();
  const { user } = useAuth();
  const [navbarOpen, setNavbarOpen] = useState(false);
  const isAdmin = useIsAdmin();

  const navbarToggleHandler = () => {
    setNavbarOpen(!navbarOpen);
  };

  const handleSignOut = useCallback(async () => {
    try {
      await signOut();
      router.push("/");
      router.refresh();
    } catch (error) {
      console.error("Error signing out:", error);
      // Still redirect to home page even if sign out fails
      router.push("/");
      router.refresh();
    }
  }, [router]);

  const isActive = (path?: string) => {
    if (!path) return false;
    // Special case for root path - only match exact pathname
    if (path === "/") {
      return pathname === "/";
    }
    // Check if current path starts with the menu item path
    // This handles cases like /400B/home matching /400B menu item
    return pathname.startsWith(path);
  };

  return (
    <header className="bg-scoutBlue h-14 text-white print:hidden">
      <div className="mx-auto px-4 h-full">
        <div className="flex justify-between items-center h-full">
          {/* Logo Section */}
          <div className="flex items-center">
            <Link href="/" className="block">
              <Image
                src="/images/bsa/scouting_america_logo.png"
                alt="logo"
                height={38}
                width={246}
              />
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={navbarToggleHandler}
            aria-label="Mobile Menu"
            className="lg:hidden p-2"
          >
            {navbarOpen ? (
              <>
                <span
                  className={`relative my-1.5 block h-0.5 w-[24px] bg-white transition-all duration-300 ${
                    navbarOpen ? "top-[7px] rotate-45" : ""
                  }`}
                />
                <span
                  className={`relative my-1.5 block h-0.5 w-[24px] bg-white transition-all duration-300 ${
                    navbarOpen ? "opacity-0" : ""
                  }`}
                />
                <span
                  className={`relative my-1.5 block h-0.5 w-[24px] bg-white transition-all duration-300 ${
                    navbarOpen ? "top-[-8px] -rotate-45" : ""
                  }`}
                />
              </>
            ) : (
              <LayoutGridIcon />
            )}
          </button>

          <nav
            className={`navbar absolute top-14 right-0 z-30 w-[200px] rounded border-[.5px] border-body-color/50 bg-scoutBlue py-1 px-2 duration-300 dark:border-body-color/20 lg:visible lg:static lg:w-auto lg:border-none lg:!bg-transparent lg:p-0 lg:opacity-100 ${
              navbarOpen
                ? "visibility top-0 opacity-100"
                : "invisible opacity-0"
            }`}
          >
            <ul className="flex flex-col lg:flex-row lg:items-center">
              {menuItems.map((menuItem) => (
                <li key={menuItem.id} className="group">
                  {menuItem.path ? (
                    <a
                      href={menuItem.path}
                      className={`
                        block py-2 px-4 text-white font-roboto hover:opacity-70 lg:inline-flex lg:py-6
                        transition-colors
                        ${isActive(menuItem.path) ? "bg-white bg-opacity-20 border-b-4 border-white" : ""}
                      `}
                    >
                      {menuItem.title}
                    </a>
                  ) : null}
                </li>
              ))}

              {/* Authentication Links */}
              {user ? (
                <>
                  {/* Mobile: Inline menu items */}
                  {isAdmin && (
                    <li className="group lg:hidden">
                      <Link
                        href="/dashboard"
                        className="flex items-center gap-2 py-2 px-4 text-white font-roboto hover:opacity-70 transition-colors"
                        onClick={() => setNavbarOpen(false)}
                      >
                        <LayoutDashboard className="w-4 h-4" />
                        <span>Dashboard</span>
                      </Link>
                    </li>
                  )}
                  <li className="group lg:hidden">
                    <Link
                      href="/profile"
                      className="flex items-center gap-2 py-2 px-4 text-white font-roboto hover:opacity-70 transition-colors"
                      onClick={() => setNavbarOpen(false)}
                    >
                      <UserIcon className="w-4 h-4" />
                      <span>Profile</span>
                    </Link>
                  </li>
                  <li className="group lg:hidden">
                    <button
                      onClick={() => {
                        setNavbarOpen(false);
                        handleSignOut();
                      }}
                      className="flex items-center gap-2 w-full py-2 px-4 text-white font-roboto hover:opacity-70 transition-colors text-left"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Logout</span>
                    </button>
                  </li>

                  {/* Desktop: Dropdown menu */}
                  <li className="hidden lg:block lg:ml-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger
                        id="user-menu"
                        className="flex items-center gap-2 py-2 px-4 text-white font-roboto hover:opacity-70 lg:inline-flex lg:py-6 transition-colors"
                      >
                        <UserIcon className="w-4 h-4" />
                        <ChevronDown className="w-4 h-4" />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="w-48" align="end">
                        {isAdmin && (
                          <DropdownMenuItem asChild>
                            <Link
                              href="/dashboard"
                              className="flex items-center gap-2 cursor-pointer"
                            >
                              <LayoutDashboard className="w-4 h-4" />
                              <span>Dashboard</span>
                            </Link>
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem asChild>
                          <Link
                            href="/profile"
                            className="flex items-center gap-2 cursor-pointer"
                          >
                            <UserIcon className="w-4 h-4" />
                            <span>Profile</span>
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          id="logout-button"
                          onClick={handleSignOut}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <LogOut className="w-4 h-4" />
                          <span>Logout</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </li>
                </>
              ) : (
                <li className="group lg:ml-2">
                  <Link
                    href="/login"
                    className="flex items-center gap-2 py-2 px-4 text-white font-roboto hover:opacity-70 lg:inline-flex lg:py-6 transition-colors"
                    onClick={() => setNavbarOpen(false)}
                  >
                    <LogIn className="w-4 h-4" />
                    <span>Login</span>
                  </Link>
                </li>
              )}
            </ul>
          </nav>
        </div>
      </div>
    </header>
  );
}
